import { time } from '$lib/core/time';
import type { BombEntity } from '$lib/onchain/types';
import { areaAt, ZONE_SIZE, zoneLocalCoord, type Position } from 'bomber-world-contracts';
import { Container, Graphics } from 'pixi.js';
import { CellType } from '../WallRenderer';
import { GameObject } from './GameObject';

// Get cell type at world coordinates
function getCellTypeAt(worldX: number, worldY: number): CellType {
	const areaLocalX = zoneLocalCoord(worldX);
	const areaLocalY = zoneLocalCoord(worldY);
	const cellIndex = areaLocalX + areaLocalY * ZONE_SIZE;

	const area = areaAt(worldX, worldY);
	return area.cells[cellIndex] as CellType;
}

// Calculate explosion tiles in 4 directions, blocked by walls
export function calculateExplosionTiles(position: Position, explosionRadius: number): Set<string> {
	const tiles = new Set<string>();
	const { x: bombX, y: bombY } = position;

	// Add center tile (bomb position)
	tiles.add(`${bombX},${bombY}`);

	// Explore in 4 directions: up, down, left, right
	const directions = [
		{ dx: 0, dy: 1 }, // up
		{ dx: 0, dy: -1 }, // down
		{ dx: 1, dy: 0 }, // right
		{ dx: -1, dy: 0 } // left
	];

	for (const { dx, dy } of directions) {
		for (let i = 1; i <= explosionRadius; i++) {
			const tileX = bombX + dx * i;
			const tileY = bombY + dy * i;

			// Check if this tile is within bounds and not blocked
			const cellType = getCellTypeAt(tileX, tileY);

			if (cellType === CellType.Wall) {
				// Walls stop explosion completely
				break;
			} else {
				// Add this tile to explosion
				tiles.add(`${tileX},${tileY}`);

				// Empty and Exit tiles allow explosion to continue
				if (cellType === CellType.Box) {
					// Boxes are affected and stop explosion
					break;
				}
			}
		}
	}

	return tiles;
}

export class BombObject extends GameObject {
	protected bombAlive: Container;
	protected bombExploded: Graphics;
	protected detonated: boolean = false;

	// Explosion state tracking
	protected explosionContainer: Graphics;
	protected explosionTiles: Set<string> = new Set();
	protected explosionEpoch: number | null = null;
	protected explosionTriggered: boolean = false;

	// Global tracking to prevent retriggering across object lifecycles
	private static explodedBombs: Set<string> = new Set();

	// Configurable explosion parameters
	private readonly explosionRadius: number = 3; // tiles in each direction
	private readonly explosionDuration: number = 1; // seconds to show explosion

	constructor(
		protected world: { pathDisplayObject: Container },
		protected entity: BombEntity
	) {
		super();

		this.bombAlive = new Container();
		const actualBombAlive = new Graphics().circle(0, 0, 4).fill({ color: 0xff0000 });
		const futureExplosion = new Graphics();
		futureExplosion.alpha = 0.7;
		this.bombAlive.addChild(futureExplosion);
		this.bombAlive.addChild(actualBombAlive);
		const tiles = calculateExplosionTiles(this.entity.position, this.explosionRadius);
		for (const tileKey of tiles) {
			const [x, y] = tileKey.split(',').map(Number);
			// Calculate pixel position relative to bomb center
			const pixelX = (x - this.entity.position.x) * 10;
			const pixelY = (y - this.entity.position.y) * 10;

			// Draw explosion circle (orange/yellow)
			futureExplosion.circle(pixelX, pixelY, 4).fill({ color: 0xff0000 }); // Orange color for explosion
		}
		this.addChild(this.bombAlive);

		this.bombExploded = new Graphics().circle(0, 0, 4).fill({ color: 0x00ff00 });
		this.addChild(this.bombExploded);
		this.bombExploded.visible = false;

		// Explosion graphics container
		this.explosionContainer = new Graphics();
		this.addChild(this.explosionContainer);
		this.explosionContainer.visible = false;
	}

	// Draw explosion graphics on calculated tiles
	private drawExplosionGraphics(): void {
		this.explosionContainer.clear();

		// Draw explosion effect on each affected tile
		for (const tileKey of this.explosionTiles) {
			const [x, y] = tileKey.split(',').map(Number);
			// Calculate pixel position relative to bomb center
			const pixelX = (x - this.entity.position.x) * 10;
			const pixelY = (y - this.entity.position.y) * 10;

			this.explosionContainer
				.moveTo(pixelX - 5, pixelY - 5)
				.lineTo(pixelX + 5, pixelY + 5)
				.moveTo(pixelX - 5, pixelY + 5)
				.lineTo(pixelX + 5, pixelY - 5)
				.stroke({ width: 1, color: 0xff0000 });
		}
	}

	// Check if we should trigger explosion and prevent retriggering
	private shouldTriggerExplosion(currentEpoch: number): boolean {
		const bombId = this.entity.id;

		// Don't trigger if this bomb has already exploded (global tracking)
		if (BombObject.explodedBombs.has(bombId)) {
			return false;
		}

		// Don't trigger if already triggered in this instance
		if (this.explosionTriggered) {
			return false;
		}

		// Don't trigger if bomb hasn't detonated yet (compare game epochs)
		if (this.entity.detonationEpoch > currentEpoch) {
			return false;
		}

		return true;
	}

	// Update explosion visibility based on timing
	private updateExplosionVisibility(currentTime: number): void {
		if (!this.explosionTriggered || this.explosionEpoch === null) {
			return;
		}

		// Hide explosion after duration
		if (currentTime > this.explosionEpoch + this.explosionDuration) {
			this.explosionContainer.visible = false;
			this.bombExploded.visible = false;
		}
	}

	update(entity: BombEntity, epoch: number) {
		this.entity = entity;

		this.zIndex = 0;

		// Check if we should trigger the explosion (using game epoch for timing)
		const shouldExplode = this.shouldTriggerExplosion(epoch);

		if (shouldExplode) {
			this.explosionTriggered = true;
			this.explosionEpoch = time.now(); // Store real time when explosion started
			this.detonated = true;

			// Register this bomb as globally exploded to prevent retriggering
			BombObject.explodedBombs.add(this.entity.id);

			// Calculate which tiles are affected by the explosion
			this.explosionTiles = calculateExplosionTiles(this.entity.position, this.explosionRadius);

			// Draw explosion graphics
			this.drawExplosionGraphics();

			// Show explosion, hide bomb
			this.explosionContainer.visible = true;
			this.bombAlive.visible = false;
			this.bombExploded.visible = false; // Keep this hidden since we use explosion graphics
		} else {
			// Check if this bomb has already exploded globally
			if (BombObject.explodedBombs.has(this.entity.id)) {
				// Keep bomb invisible after explosion
				this.bombAlive.visible = false;
				this.bombExploded.visible = false;
				this.explosionContainer.visible = false;
			}
		}

		// Position the bomb/explosion
		const destination = {
			x: 10 * entity.position.x,
			y: 10 * entity.position.y
		};
		this.x = destination.x;
		this.y = destination.y;
	}

	// Per-frame update for explosion visibility timing
	tick() {
		// Update explosion visibility based on real time duration
		this.updateExplosionVisibility(time.now());
	}

	onRemoved() {}
}
